import Baobab, { type BaobabOptions, type Cursor } from 'baobab';
import dispatchr from 'dispatchr';
import { isSystemTest } from '@/core/utilities';
import type { BaobabEvent } from '@/core/flux.types';
import type { AnyProperty } from '@/utilities.types';

// this is needed for Baobab in dev mode
if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
  (globalThis as typeof globalThis & { global?: typeof globalThis }).global = globalThis;
}

// Original code was from the flux-angular package: https://github.com/christianalfoni/flux-angular

const immutableDefaults: Partial<BaobabOptions> = {
  immutable: process.env.NODE_ENV !== 'production', // Speed up production by not freezing objects
  asynchronous: process.env.NODE_ENV !== 'test' && !isSystemTest(), // Changes to the tree emit synchronously
  pure: process.env.NODE_ENV !== 'test', // Allows dispatches to still trigger events even if value does not change
};

type StoreConstructor<T extends Store = Store> = {
  new (...args: any[]): T;
  storeName: string;
};

interface DispatcherContextValue {
  getContext: () => { immutableDefaults: Partial<BaobabOptions> };
  getStore: <T extends Store>(store: StoreConstructor<T> | string) => T;
  waitFor: (stores: string | string[], callback: () => void) => void;
}

type DispatchFn = (action: string, payload?: unknown, option?: PushOption) => void;

interface DispatcherState {
  stores: Record<string, any>;
}

interface DispatcherContext {
  dispatch: DispatchFn;
  getStore: <T extends Store>(store: StoreConstructor<T> | string) => T;
  waitFor: (stores: string | string[], callback: () => void) => void;
  dehydrate: () => DispatcherState;
  rehydrate: (state: DispatcherState) => void;
  storeInstances: Record<string, Store>;
}

type HandlerRegistration = {
  name: string;
  handler: string | ((payload: unknown) => void);
};

type ListenerCallback = (baobabEvent: BaobabEvent) => void;

interface DispatchrInstance {
  createContext: (context?: Record<string, unknown>) => DispatcherContext;
  getStoreName: (store: StoreConstructor | string) => string;
  registerStore: (store: StoreConstructor) => void;
  isRegistered: (storeName: StoreConstructor | string) => boolean;
  _registerHandler: (action: string, storeName: string, handler: HandlerRegistration['handler']) => void;
  handlers: Record<string, HandlerRegistration[]>;
  stores: Record<string, StoreConstructor>;
}

interface DispatchrFactory {
  createDispatcher: (options?: Record<string, unknown>) => DispatchrInstance;
}

/**
 * @type PersistenceLevel
 * Different stores are persisted at different locations. Each persistence level is a group of stores that are
 * stored together
 *
 * NONE      - not saved, stored in memory only
 * WORKBENCH - per-user data store
 * WORKBOOK  - per-workbook data store
 * WORKSHEET - per-worksheet data store (in workstep)
 *
 * Every store should have a property called 'persistenceLevel' assigned one of PersistenceLevel.
 */
export type PersistenceLevel = 'NONE' | 'WORKBENCH' | 'WORKBOOK' | 'WORKSHEET';

export type InitializeMode = 'FORCE' | 'SOFT';

export const PERSISTENCE_LEVELS = ['NONE', 'WORKBENCH', 'WORKBOOK', 'WORKSHEET'] as const;

export const PUSH_IGNORE = 'PUSH_IGNORE' as const;
export const PUSH_WORKSTEP = 'PUSH_WORKSTEP' as const;
export const PUSH_WORKSTEP_IMMEDIATE = 'PUSH_WORKSTEP_IMMEDIATE' as const;
export const PUSH_WORKBOOK = 'PUSH_WORKBOOK' as const;
export const PUSH_WORKBENCH = 'PUSH_WORKBENCH' as const;
export type PushOption =
  | typeof PUSH_IGNORE
  | typeof PUSH_WORKSTEP
  | typeof PUSH_WORKSTEP_IMMEDIATE
  | typeof PUSH_WORKBOOK
  | typeof PUSH_WORKBENCH;

export interface FluxStore<T extends object> {
  handlers: Record<string, string>;

  exports: T;

  initialize: (initializeMode?: InitializeMode) => void;

  dehydrate?: () => unknown;

  rehydrate?: (state: unknown) => void;
}

export abstract class Store {
  static readonly storeName: string;
  readonly rehydrateWaitFor?: string[];
  private readonly dispatcher: DispatcherContextValue;
  private readonly immutableDefaults: Partial<BaobabOptions>;
  public state!: Baobab;
  abstract persistenceLevel: PersistenceLevel;
  protected abstract readonly handlers: Record<string, (payload: any) => void>;

  constructor(dispatcher: DispatcherContextValue) {
    this.dispatcher = dispatcher;
    this.immutableDefaults = dispatcher.getContext().immutableDefaults;
    this.initialize();
  }

  protected monkey = Baobab.monkey;

  protected immutable(initialState: AnyProperty, options: Partial<BaobabOptions> = {}): Baobab {
    if (this.state) {
      this.state.set(initialState);
      return this.state;
    } else {
      return new Baobab(initialState, {
        ...this.immutableDefaults,
        ...options,
      });
    }
  }

  abstract initialize(initializeMode?: InitializeMode): void;

  rehydrate(_state: AnyProperty) {}

  public getHandlers(): Record<string, (payload: any) => void> {
    return this.handlers;
  }

  protected waitFor(stores: string | string[], callback: () => void) {
    const storeList = Array.isArray(stores) ? stores : [stores];
    try {
      storeList.forEach((store) => this.dispatcher.getStore(store));
    } catch {
      throw new Error(`Waiting for stores that are not created is not allowed: ${storeList.join(', ')}.`);
    }

    this.dispatcher.waitFor(storeList, callback.bind(this));
  }
}

// Flux Service is a wrapper for the Yahoo Dispatchr
export class FluxService {
  public useEvalAsync = process.env.NODE_ENV !== 'test';
  stores: FluxStore<Record<string, unknown>>[] = [];
  public readonly pendingStores: string[] = [];
  private dispatcherInstance: DispatchrInstance;
  private immutableDefaults: Partial<BaobabOptions> = immutableDefaults;
  private dispatchCallback: DispatchFn = () => {};
  public dispatcher: DispatcherContext;

  constructor() {
    const dispatcherFactory = dispatchr as unknown as DispatchrFactory;
    this.dispatcherInstance = dispatcherFactory.createDispatcher();
    this.dispatcher = this.dispatcherInstance.createContext({
      immutableDefaults: this.immutableDefaults,
    });
  }

  dispatch(handler: string, payload?: unknown, option?: PushOption) {
    if (this.pendingStores.length && process.env.NODE_ENV !== 'test') {
      throw new Error(`Stores have not yet been injected: ${this.pendingStores.join(', ')}`);
    }
    this.dispatcher.dispatch(handler, payload, option);
    this.dispatchCallback(handler, payload, option);
  }

  setDispatchCallback(callback: DispatchFn) {
    this.dispatchCallback = callback;
  }

  addPendingStore(storeName: string) {
    this.pendingStores.push(storeName);
  }

  createStore<T extends Store>(klazz: StoreConstructor<T>): T {
    const storeName = this.dispatcherInstance.getStoreName(klazz);
    // Needed to accommodate jest tests that load the flux stores again
    if (this.dispatcherInstance.isRegistered(storeName)) {
      return this.dispatcher.getStore(storeName);
    }

    this.dispatcherInstance.registerStore(klazz);
    const store = this.dispatcher.getStore(klazz);
    // We don't want the handlers to be static, so register them manually
    const handlers = store.getHandlers();
    Object.keys(handlers).forEach((action) => {
      const handler = handlers[action];
      this.dispatcherInstance._registerHandler(action, storeName, handler);
    });
    return store;
  }

  listenTo(storeExport: Store, callback: ListenerCallback): () => void;
  listenTo(
    storeExport: Store,
    mapping: string[] | undefined,
    callback: ListenerCallback,
    useEvalAsync?: boolean,
  ): () => void;
  listenTo(
    storeExport: Store,
    mappingOrCallback?: string[] | ListenerCallback,
    callback?: ListenerCallback,
    useEvalAsync = this.useEvalAsync,
  ) {
    if (typeof mappingOrCallback === 'function' && !callback) {
      return listenToStore(this, storeExport, undefined, mappingOrCallback, useEvalAsync);
    }

    if (!callback) {
      throw new Error('A callback must be provided to listenTo');
    }

    return listenToStore(this, storeExport, mappingOrCallback as string[] | undefined, callback, useEvalAsync);
  }

  getStore<T extends Store>(storeOrExports: StoreConstructor<T> | T): T {
    const storeConstructor =
      typeof storeOrExports === 'function'
        ? (storeOrExports as StoreConstructor<T>)
        : (storeOrExports.constructor as StoreConstructor<T>);

    return this.dispatcher.getStore(storeConstructor);
  }

  setImmutableDefaults(defaults: Partial<BaobabOptions>) {
    this.immutableDefaults = defaults;
  }

  reset() {
    this.dispatcherInstance.handlers = {};
    Object.keys(this.dispatcher.storeInstances).forEach((storeName) => {
      // Old stores will be re-injected and so must be removed
      if ((this.dispatcher.storeInstances[storeName].constructor as any).exports) {
        delete this.dispatcher.storeInstances[storeName];
        delete this.dispatcherInstance.stores[storeName];
      } else {
        const store = this.dispatcher.storeInstances[storeName];
        store.initialize('FORCE');
        const handlers = store.getHandlers();
        Object.keys(handlers).forEach((action) => {
          const handler = handlers[action];
          this.dispatcherInstance._registerHandler(action, storeName, handler);
        });
      }
    });
    this.stores = [];
  }
}

function listenToStore(
  flux: FluxService,
  storeExport: Store,
  mapping: string[] | undefined,
  callback: ListenerCallback,
  useEvalAsync: boolean,
): () => void {
  const store = flux.getStore(storeExport);

  if (!store.state) {
    const storeName = (storeExport.constructor as StoreConstructor).storeName;
    throw new Error(
      `Store ${storeName} has not defined state with this.immutable() which is required in order to use $listenTo`,
    );
  }

  const cursor: Cursor = (mapping ? store.state.select(mapping) : store.state) as Cursor;

  const handler: ListenerCallback = useEvalAsync ? (event) => callback(event) : callback;
  const emitterHandler = handler as unknown as (event: unknown) => void;

  cursor.on('update', emitterHandler);

  return () => cursor.off('update', emitterHandler);
}
